import { RouterProvider } from 'react-router-dom';
import { ConfigProvider, theme as antTheme } from 'antd';
import { Provider, useAtom } from 'jotai';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import React from 'react';
import './App.css';
import Router from './routes/Router';
import { themeAtom, useThemeTokens } from './states/theme.atom';
import ErrorFallback from './components/common/ErrorFallback';
import i18n from './i18n';
import { AuthProvider } from './components/auth/AuthProvider';
import { isProd } from './utils/isProd.util';
import { initSentry } from './services/sentry.service';

// Create a new client with debugging configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

// Initialize Sentry
initSentry();

// Error handler function
const handleError = (error: Error, info: React.ErrorInfo) => {
  console.error(error, info);
  // Sentry error logging is handled by the ErrorBoundary component
};

const AppContent = () => {
  const [theme] = useAtom(themeAtom);
  const tokens = useThemeTokens();

  return (
    <ConfigProvider
      direction={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      key={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      theme={{
        algorithm: theme === 'dark' ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: tokens.tokens,
        components: {
          Layout: {
            ...tokens.tokensLayout,
          },
          Tooltip: {
            ...tokens.tokensTooltip,
          },
          Tree: {
            ...tokens.tokensTree,
          },
        },
      }}
    >
      <div>
        <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
          <RouterProvider router={Router} />
        </ErrorBoundary>
      </div>
    </ConfigProvider>
  );
};

// Main App component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Provider>
          <AppContent />
        </Provider>
      </AuthProvider>
      {!isProd() && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
